import React from "react"
import translatedStrings from "../strings/strings.json"
import translatedAria from "../strings/aria.json"

// content strings — visible UI text. Use via <LocalizedString> or localeString().
// aria strings  — accessible-name strings for icon-only controls. Use ariaLabel().
// Keeping them in separate JSON files (and behind separate APIs) keeps translator
// workflows and intent clear, and avoids accidental key collisions.

const findExact = (translations, key, locale) =>
    locale && translations
      .find(s => s.key === key)?.locales
      .find(t => t.locale === locale)

const baseLocale = (locale) => {
    if (locale && locale.includes("-")) {
        return locale.split("-")[0]
    }
}

const stringWithExactLocale = (key, locale) =>
    findExact(translatedStrings.translations, key, locale)

const stringWithLocale = (key, locale) =>
    stringWithExactLocale(key, locale) || stringWithExactLocale(key, baseLocale(locale))

const ariaWithLocale = (key, locale) =>
    findExact(translatedAria.translations, key, locale)
      || findExact(translatedAria.translations, key, baseLocale(locale))

export const localeString = (string, locale) => {
    return stringWithLocale(string, locale) || { locale: "en", translation: string }
}

/**
 * Plain-string accessible-name lookup for aria-label etc. Falls back to the
 * key (English) when no translation matches. Returns a bare string so it
 * can be passed straight into aria-label / aria-description without
 * unwrapping a .translation property.
 */
export const ariaLabel = (key, locale) =>
    ariaWithLocale(key, locale)?.translation || key

function isRtlLocale(locale) {
    return locale === "he" || locale === "ar"
}

export const LocalizedString = ({ string, locale, className, ...props }) => {
    const translatedString = localeString(string, locale)
    const shortLangCode = translatedString.locale.substr(0, 2)
    return <span {...props} className={className + " localized-string"} lang={shortLangCode} dir={isRtlLocale(translatedString.locale) ? "rtl" : "ltr"}>{translatedString.translation}</span>
}
